import React, { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';

import '@/assets/styles/components/meet-interaction-feedback.styles.scss';
import { Button, Checkbox } from '@/components';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { CoachExpertInteractionFeedbackCreateRequest, CoachExpertInteractionFeedbackUpdateRequest, InteractionLabelResponse } from '@dopingtech/live_expert_bff_api_ts';
import { toast } from 'react-toastify';
import { CoachExpertInteractionFeedbackUpdateRequestConnectionStatusEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';

interface MeetingInteractionFeedbackProps {
	interactionId: string;
	interactionFeedback: any;
	connectionStatus: CoachExpertInteractionFeedbackUpdateRequestConnectionStatusEnum;
}

const MeetingInteractionFeedback: React.FC<MeetingInteractionFeedbackProps> = ({ interactionId, interactionFeedback, connectionStatus }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedOptions, setSelectedOptions] = useState<InteractionLabelResponse[]>([]);
	const dropdownRef = useRef<HTMLDivElement | null>(null);

	const { FeedbackControllerApi } = useApi();

	const createFeedback = useMutationProvider((body: CoachExpertInteractionFeedbackCreateRequest) => FeedbackControllerApi.createCoachExpertInteractionFeedback(body), {
		onSuccess: () => {
			refetchCoachExpertInteractionFeedback();
			toast.success('Geri bildirim başarıyla kaydedildi.');
		}
	});

	const updateFeedback = useMutationProvider(
		({ feedbackId, body }: { feedbackId: number; body: CoachExpertInteractionFeedbackUpdateRequest }) => FeedbackControllerApi.updateCoachExpertInteractionFeedback(feedbackId, body),
		{
			onSuccess: () => {
				refetchCoachExpertInteractionFeedback();
				toast.success('Geri bildirim başarıyla güncellendi.');
			}
		}
	);

	const { data: interactionLabels } = useQueryProvider([QUERY_KEYS.getInteractionLabels], () => FeedbackControllerApi.getInteractionLabels(), {
		refetchOnWindowFocus: false,
		select: ({ data }) => data?.data
	});

	const { data: coachExpertInteractionFeedback, refetch: refetchCoachExpertInteractionFeedback } = useQueryProvider(
		[QUERY_KEYS.findCoachExpertInteractionFeedbackByInteractionId, interactionFeedback?.id],
		() => FeedbackControllerApi.findCoachExpertInteractionFeedbackByInteractionId(interactionFeedback?.id),
		{
			refetchOnWindowFocus: false,
			select: ({ data }) => data?.data,
			enabled: !!interactionFeedback?.id
		}
	);

	const handleCheckboxChange = (option: InteractionLabelResponse) => {
		setSelectedOptions((prev: InteractionLabelResponse[]) => {
			if (prev.includes(option)) {
				return prev.filter(item => item.id !== option.id);
			} else {
				return [...prev, option];
			}
		});
	};

	const onSaveFeedback = async () => {
		const interactionLabels = selectedOptions.length > 0 ? selectedOptions.map(i => i.name).join(',') : '';
		if (coachExpertInteractionFeedback?.id) {
			await updateFeedback.mutateAsync({
				feedbackId: coachExpertInteractionFeedback?.id as number,
				body: {
					interactionLabel: interactionLabels || '',
					connectionStatus: connectionStatus
				}
			});
		} else {
			await createFeedback.mutateAsync({
				interactionId: interactionId,
				interactionLabel: interactionLabels || '',
				connectionStatus: connectionStatus
			});
		}
		setIsOpen(false);
	};

	const onCancelFeedback = () => {
		if (interactionLabels && coachExpertInteractionFeedback?.interactionLabel) {
			const labels = coachExpertInteractionFeedback.interactionLabel.split(',');
			const matchedOptions = interactionLabels?.filter(label => labels.includes(label?.name as string)) || [];

			setSelectedOptions(matchedOptions);
		} else {
			setSelectedOptions([]);
		}

		setIsOpen(false);
	};

	useEffect(() => {
		if (interactionLabels && coachExpertInteractionFeedback?.interactionLabel) {
			const labels = coachExpertInteractionFeedback.interactionLabel.split(',');
			const matchedOptions = interactionLabels?.filter(label => labels.includes(label?.name as string)) || [];
			setSelectedOptions(matchedOptions);
		}
	}, [interactionLabels, coachExpertInteractionFeedback?.interactionLabel]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent): void => {
			if (dropdownRef?.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	const rowsPerColumn: number = interactionLabels ? Math.ceil(interactionLabels.length / 2) : 0;

	const displayText = selectedOptions.length > 0 ? selectedOptions.map((i: any) => i.name).join(', ') : 'Seçeneklerden uygun olanları seçin';

	return (
		<div className="meet-interaction-feedback">
			<div className="meet-interaction-feedback__header">
				<div className="title">Yaptığınız görüşmeye ilişkin aşağıdaki durumlardan uygun olanları seçin</div>
			</div>

			<div className="meet-interaction-feedback__content">
				<div
					className="dropdown"
					ref={dropdownRef}>
					<button
						className="dropdown__button"
						onClick={() => setIsOpen(!isOpen)}>
						<span
							className={clsx('dropdown__button-text', {
								'dropdown__button-text--selected': selectedOptions.length > 0
							})}>
							{displayText}
						</span>
						<span className={`dropdown__icon ${isOpen ? 'dropdown__icon--open' : ''}`}>
							<ChevronDown />
						</span>
					</button>

					<div className={`dropdown__content ${isOpen ? 'dropdown__content--open' : ''}`}>
						<div className="info-text">
							<i className="icons-info-circle info-icon" />
							<p>Birden fazla seçim yapabilirsin</p>
						</div>

						<div
							className="feedback-options"
							style={{ '--rows-per-column': rowsPerColumn } as React.CSSProperties}>
							{interactionLabels?.map(option => (
								<div
									className="option"
									key={option.id}>
									<Checkbox
										label={option.name}
										checked={selectedOptions.some(o => o.id === option.id)}
										onChange={() => handleCheckboxChange(option)}
										size="small"
									/>
								</div>
							))}
						</div>

						<div className="actions">
							<Button
								onClick={() => onSaveFeedback()}
								size="small">
								Kaydet
							</Button>
							<Button
								onClick={onCancelFeedback}
								size="small"
								variant="secondary">
								Vazgeç
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MeetingInteractionFeedback;
